import { useReducer, useState } from "react";
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import {
  RecentReportQueryValidation,
  useDownloadOpenRecentContractData,
} from "@/lib/api/otc";
import { SeparatorWithText } from "@/components/ui/separator";
import { BearDatePicker } from "../ContractForm";
import { ReportCard, ReportDownloadLink } from "./ReportCard";

interface RecentRange {
  start?: Date;
  end?: Date;
}

interface RecentFiltersState {
  created_date?: Date;
  created_range?: RecentRange;
}

type RecentFiltersAction =
  | { type: "setCreatedDate"; value: Date | undefined }
  | { type: "setRangeStart"; value: Date | undefined }
  | { type: "setRangeEnd"; value: Date | undefined };

function recentFiltersReducer(
  state: RecentFiltersState,
  action: RecentFiltersAction,
): RecentFiltersState {
  switch (action.type) {
    case "setCreatedDate":
      // Selecting a single created date clears any range selection.
      return { created_date: action.value, created_range: undefined };
    case "setRangeStart": {
      const start = action.value;
      const currentEnd = state.created_range?.end;
      // Mirror previous behaviour: when starting a range and no end exists yet,
      // default the end to the end of the same month as the start.
      const end = start && !currentEnd ? moment(start).endOf("month").toDate() : currentEnd;
      return {
        created_date: undefined,
        created_range: { start, end },
      };
    }
    case "setRangeEnd":
      return {
        created_date: undefined,
        created_range: { start: state.created_range?.start, end: action.value },
      };
    default:
      return state;
  }
}

interface RecentLinks {
  link: string;
  import_new_contracts_link: string;
  import_update_contracts_link: string;
}

/**
 * Download Recent Contracts Report — by single created date OR by created range.
 * Produces 3 download links.
 */
export function DownloadRecentContractsCard() {
  const { toast } = useToast();
  const downloadDataLinkFunc = useDownloadOpenRecentContractData();
  const [filters, dispatch] = useReducer(recentFiltersReducer, { created_date: new Date() });
  const [links, setLinks] = useState<RecentLinks | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const isDisabled =
    !filters.created_date &&
    !filters.created_range?.start &&
    !filters.created_range?.end;

  const onGenerate = async () => {
    if (isDownloading || isDisabled) return;
    setLinks(undefined);
    setIsDownloading(true);

    try {
      const queryResult = RecentReportQueryValidation.safeParse({
        created_date: filters.created_date
          ? moment(filters.created_date).format("YYYY/MM/DD")
          : undefined,
        created_range:
          filters.created_range?.start || filters.created_range?.end
            ? {
                start: filters.created_range?.start
                  ? moment(filters.created_range.start).format("YYYY/MM/DD")
                  : undefined,
                end: filters.created_range?.end
                  ? moment(filters.created_range.end).format("YYYY/MM/DD")
                  : undefined,
              }
            : undefined,
      });

      if (!queryResult.success) {
        toast({ title: "Invalid Filters", description: "" });
        return;
      }

      const result = await downloadDataLinkFunc(queryResult.data);
      if (!result.success) {
        throw result;
      }
      setLinks({
        link: result.data.link,
        import_new_contracts_link: result.data.import_new_contracts_link,
        import_update_contracts_link: result.data.import_update_contracts_link,
      });
    } catch (error: unknown) {
      console.error("Failed to generate otc recent report", error);
      toast({ title: "Failed to generate", description: "" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ReportCard
      title="Recent Contracts Report"
      description="Download recently created contracts by a single created date, or by a created date range."
      isDownloading={isDownloading}
      isGenerateDisabled={isDisabled}
      onGenerate={onGenerate}
      hasResults={!!links}
      linkContent={
        links ? (
          <>
            <ReportDownloadLink href={links.link} label="Download Report" />
            <ReportDownloadLink
              href={links.import_new_contracts_link}
              label="Download Import"
            />
            <ReportDownloadLink
              href={links.import_update_contracts_link}
              label="Download Import Updated"
            />
          </>
        ) : null
      }
    >
      <div className="flex flex-col gap-3">
        <BearDatePicker
          tabIndex={0}
          name="recent_download_filters_created_date"
          label="Single Created Date"
          value={filters.created_date}
          onValueChange={(date: Date | undefined) =>
            dispatch({ type: "setCreatedDate", value: date })
          }
        />
        <SeparatorWithText>OR</SeparatorWithText>
        <div className="flex flex-col lg:grid lg:grid-cols-1 gap-3">
          <BearDatePicker
            tabIndex={1}
            name="recent_download_filters_created_start"
            label="Created Start"
            value={filters.created_range?.start}
            onValueChange={(date: Date | undefined) =>
              dispatch({ type: "setRangeStart", value: date })
            }
          />
          <BearDatePicker
            tabIndex={2}
            name="recent_download_filters_created_end"
            label="Created End"
            value={filters.created_range?.end}
            onValueChange={(date: Date | undefined) =>
              dispatch({ type: "setRangeEnd", value: date })
            }
          />
        </div>
      </div>
    </ReportCard>
  );
}
